import React, { useState, useEffect } from 'react';
import { List, Search, Filter } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const VoucherList = () => {
  const [vouchers, setVouchers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVouchers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('business_token');
      const response = await axios.get(
        `${API}/api/business/vouchers/my-vouchers?status=${filter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVouchers(response.data);
    } catch (err) {
      console.error('Failed to fetch vouchers:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredVouchers = vouchers.filter(v =>
    v.voucher_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.deal_title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex gap-2">
          <FilterButton active={filter === 'all'} onClick={() => setFilter('all')} label="All" />
          <FilterButton active={filter === 'active'} onClick={() => setFilter('active')} label="Active" />
          <FilterButton active={filter === 'redeemed'} onClick={() => setFilter('redeemed')} label="Redeemed" />
          <FilterButton active={filter === 'expired'} onClick={() => setFilter('expired')} label="Expired" />
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#2C2416]/40" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by code, customer, or deal..."
            className="w-full pl-10 pr-4 py-2 border-2 border-[#2C2416]/20 rounded-lg focus:ring-2 focus:ring-[#8B3A3A] focus:border-transparent"
          />
        </div>
      </div>

      {/* Voucher List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B3A3A] mx-auto"></div>
          <p className="font-utility text-[#2C2416]/60 mt-4">Loading vouchers...</p>
        </div>
      ) : filteredVouchers.length === 0 ? (
        <div className="text-center py-12">
          <List size={48} className="text-[#2C2416]/20 mx-auto mb-4" />
          <p className="font-utility text-[#2C2416]/60">No vouchers found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredVouchers.map((voucher) => (
            <VoucherCard key={voucher.id} voucher={voucher} />
          ))}
        </div>
      )}
    </div>
  );
};

const FilterButton = ({ active, onClick, label }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg font-utility text-sm font-medium transition-colors ${
      active
        ? 'bg-[#8B3A3A] text-[#F5F0E1]'
        : 'bg-[#E8E2D2] text-[#2C2416] hover:bg-[#D8D2C2]'
    }`}
  >
    {label}
  </button>
);

const VoucherCard = ({ voucher }) => {
  const statusColors = {
    active: 'bg-green-100 text-green-700 border-green-300',
    redeemed: 'bg-blue-100 text-blue-700 border-blue-300',
    expired: 'bg-red-100 text-red-700 border-red-300'
  };

  return (
    <div className="bg-white border-2 border-[#2C2416]/10 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="font-utility font-bold text-[#2C2416]">{voucher.deal_title}</h4>
          <p className="font-utility text-sm text-[#2C2416]/70">{voucher.customer_name}</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${statusColors[voucher.status]}`}>
          {voucher.status}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <p className="font-utility text-xs text-[#2C2416]/60">Code</p>
          <p className="font-mono font-medium text-[#2C2416]">{voucher.voucher_code}</p>
        </div>
        <div>
          <p className="font-utility text-xs text-[#2C2416]/60">Amount</p>
          <p className="font-utility font-medium text-[#2C2416]">${voucher.amount_paid.toFixed(2)}</p>
        </div>
        <div>
          <p className="font-utility text-xs text-[#2C2416]/60">
            {voucher.status === 'redeemed' ? 'Redeemed' : 'Expires'}
          </p>
          <p className="font-utility font-medium text-[#2C2416]">
            {new Date(voucher.status === 'redeemed' ? voucher.redeemed_date : voucher.expiration_date).toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VoucherList;
